/**
 * DragTextPrint - Figma-matching print renderer for drag-text quizzes
 * Layout: PrintQuizTitle → image → problem (inline blank boxes h-40px w-88px) → word bank ("보기" box)
 */

import type { ReactNode } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import type { DragTextQuizData } from '../../../types/legacyQuiz'
import { formatProblem, countBlanks } from '../../../utils/quiz-parser'
import { PRINT_CORRECT_ANSWER_CLASS, PrintQuizTitle, PrintQuizImage, PrintWordBank, getStyles, getImageUrl, getPrintScale } from './print-common'

interface PrintQuizProps {
  quizId: string | number
  quiz: DragTextQuizData & { choices?: { key?: string | null; answer: string }[]; answers?: Array<string | { key?: string | null; answer: string }> }
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

export function DragTextPrint({
  quizId,
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: PrintQuizProps) {
  const s = getStyles(compact)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)
  const blankCount = countBlanks(quiz.problem)

  // Build option label lookup
  const optionLabelByKey = new Map<string, string>()
  if (quiz.choices && Array.isArray(quiz.choices)) {
    quiz.choices.forEach(c => {
      if (c?.key && c?.answer) optionLabelByKey.set(c.key, c.answer)
    })
  }
  if (quiz.pairs?.right && Array.isArray(quiz.pairs.right)) {
    quiz.pairs.right.forEach(c => {
      if (c?.key && c?.answer) optionLabelByKey.set(c.key, c.answer)
    })
  }

  const resolveLabel = (value: string) => optionLabelByKey.get(value) ?? value

  // Build word bank items
  const wordBankItems: { key: string; label: string }[] = (() => {
    if (quiz.choices?.length) {
      return quiz.choices
        .filter((c): c is { key: string; answer: string } => Boolean(c) && typeof c.key === 'string' && typeof c.answer === 'string')
        .map(c => ({ key: c.key, label: c.answer }))
    }
    if (quiz.pairs?.right?.length) {
      return quiz.pairs.right
        .filter((c): c is { key: string; answer: string } => Boolean(c) && typeof c.key === 'string' && typeof c.answer === 'string')
        .map(c => ({ key: c.key, label: c.answer }))
    }
    if (quiz.answers?.length) {
      return quiz.answers
        .map((a) => {
          if (typeof a === 'string') return { key: a, label: a }
          return { key: a.key || a.answer, label: a.answer }
        })
        .filter((a): a is { key: string; label: string } => typeof a.key === 'string' && a.key.length > 0)
    }
    return []
  })()

  // Render problem with blank boxes
  const renderedProblem: ReactNode[] = blankCount > 0
    ? formatProblem(
        quiz.problem,
        `${quizId}-print-problem`,
        (index: number) => {
          const answer = correctAnswers?.[index]
          const label = answer ? resolveLabel(answer) : null
          const showAnswer = showCorrectAnswers && label

          return (
            <span
              key={`blank-print-${quizId}-${index}`}
              className={cn(
                s.inputBoxLg,
                `inline-flex items-center justify-center ${compact ? 'mx-[8px] my-[4px]' : 'mx-[12px]'} px-[8px] align-middle text-[16px]`,
                showAnswer ? PRINT_CORRECT_ANSWER_CLASS : ''
              )}
            >
              {showAnswer ? <KatexText text={label} /> : '\u00A0'}
            </span>
          )
        },
        quiz.additionalImages ?? undefined
      )
    : []

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {/* Image */}
      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      {/* Problem with inline blanks — needs taller line-height to fit inline boxes */}
      {blankCount > 0 && (
        <div className={s.problemArea}>
          <div className={cn(
            s.problemLine,
            compact
              ? 'text-[15px] text-black tracking-[-0.09px] font-normal leading-[41px]'
              : s.problemText
          )}>
            {renderedProblem}
          </div>
        </div>
      )}

      {/* Word bank */}
      <div className="pl-[40px] w-full">
        <PrintWordBank items={wordBankItems} compact={compact} />
      </div>
    </div>
  )
}
